// ---------------------------------------------------------------------------
// Past Tour stream / replay (`/past-tour/{slug}/stream`).
//
// A deliberately minimal, standalone replay page — NOT a reuse of the active
// `/tour/{slug}/stream` route (which is ~3.5k lines of live-state machinery
// that does not apply to finished events). All we need here is replay: the
// Wrap It Ceremony and, when it exists, the Dance Again showcase.
//
// Resolves the slug against the `?type=past` feed ONLY. That feed embeds the
// per-event VOD URLs and availability up front (issue #2 contract), so there is
// no eventId sub-fetch and no live-status call. Resolving within the past feed
// only is the collision fix: a repeating city lands on last season's replay.
//
// Raw-VOD rule (PRD): the VOD URL only ever appears as the server-rendered
// iframe `src`. We do not expose it as a user-facing link — Watch always routes
// through this page.
// ---------------------------------------------------------------------------

import { useState } from 'react'
import { Link, useLoaderData } from 'react-router'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '#app/components/ui/tabs.tsx'
import { getTourDates } from '#app/utils/dmapi.server.ts'
import { type Route } from './+types/past-tour.$slug_.stream.ts'

type TourEvent = {
	id: string
	name: string
	slug: string
	// Past-only fields (see the locked contract in issue #2):
	wrapItVodUrl: string | null
	danceAgainVodUrl: string | null
}

type TourData = {
	tour: { id: string; name: string; years: string } | null
	events: TourEvent[]
}

const TAB_TRIGGER_CLASS =
	'border-2 border-transparent bg-transparent px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-all hover:bg-black/5 data-[state=active]:border-accent data-[state=active]:bg-accent/10 data-[state=active]:text-accent sm:px-6 sm:py-3 sm:text-sm md:px-8 md:py-4 md:text-base'

export const meta: Route.MetaFunction = ({ data }) => {
	const eventName = data?.eventName ?? 'Event'
	return [
		{ title: `Watch | ${eventName} | DanceMakers` },
		{
			name: 'description',
			content: `Watch the replay of the DanceMakers ${eventName} Wrap It Ceremony and Dance Again showcase.`,
		},
	]
}

export async function loader({ params }: Route.LoaderArgs) {
	const { slug } = params
	const apiUrl = process.env.DMAPI_URL || 'http://localhost:3001'

	const tourData = await getTourDates<TourData>(apiUrl, 'past')

	const event = tourData?.events.find((e) => e.slug === slug)
	if (!event) {
		throw new Response('Event not found', { status: 404 })
	}

	return {
		slug,
		tourName: tourData?.tour?.name ?? null,
		tourYears: tourData?.tour?.years ?? null,
		eventName: event.name,
		wrapItVodUrl: event.wrapItVodUrl,
		danceAgainVodUrl: event.danceAgainVodUrl,
	}
}

// Replay player: an embedded VOD in an aspect-video frame. Mirrors the active
// route's player styling but omits the live badge and the raw-URL "open in new
// tab" link — the VOD URL stays inside the iframe src only.
function VodPlayer({ title, vodUrl }: { title: string; vodUrl: string }) {
	return (
		<div className="relative overflow-hidden border border-muted-foreground/30">
			<div className="border-b border-border bg-muted/50 px-4 py-4 sm:px-6">
				<h3
					className="text-lg font-bold uppercase tracking-wide sm:text-xl"
					style={{ fontFamily: 'var(--font-display)' }}
				>
					{title}
				</h3>
			</div>
			<div className="aspect-video bg-black">
				<iframe
					src={vodUrl}
					className="h-full w-full"
					allowFullScreen
					allow="autoplay; fullscreen"
					title={title}
				/>
			</div>
			<div className="border-t border-border/50 bg-muted/30 px-4 py-3 sm:px-6">
				<p className="text-center text-xs text-muted-foreground">
					On-demand playback available
				</p>
			</div>
		</div>
	)
}

export default function PastEventStream() {
	const { tourName, tourYears, eventName, wrapItVodUrl, danceAgainVodUrl } =
		useLoaderData<typeof loader>()

	// Back link carries the season (tour name + years), matching the past-tour
	// results header; the event name is the heading.
	const tourLabel =
		tourYears && tourName ? `${tourYears} ${tourName}` : tourName
	const backLabel = tourLabel ?? 'Past Tour'

	const hasWrapIt = Boolean(wrapItVodUrl)
	const hasDanceAgain = Boolean(danceAgainVodUrl)
	const [activeTab, setActiveTab] = useState(
		hasWrapIt ? 'wrapit' : 'danceagain',
	)

	return (
		<div className="min-h-screen bg-background pb-16 pt-24">
			{/* Compact Header */}
			<section className="border-b border-border/30">
				<div className="container px-4 py-3 sm:px-6 md:px-16 md:py-4">
					<Link
						to="/past-tour"
						className="inline-flex items-center gap-2 text-muted-foreground transition-colors hover:text-accent"
					>
						<svg
							className="h-4 w-4"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M15 19l-7-7 7-7"
							/>
						</svg>
						<span className="text-sm sm:text-base">{backLabel}</span>
					</Link>
					<h1
						className="mt-2 text-3xl font-bold uppercase text-foreground md:text-5xl"
						style={{ fontFamily: 'var(--font-display)' }}
					>
						{eventName}
					</h1>
				</div>
			</section>

			<section className="container px-6 py-10 md:px-16">
				{!hasWrapIt && !hasDanceAgain ? (
					<div className="py-16 text-center">
						<h2
							className="mb-2 text-lg font-bold uppercase sm:text-xl"
							style={{ fontFamily: 'var(--font-display)' }}
						>
							Replay Not Available
						</h2>
						<p className="text-sm text-muted-foreground">
							This event&rsquo;s replay isn&rsquo;t available.
						</p>
					</div>
				) : hasWrapIt && hasDanceAgain ? (
					<Tabs value={activeTab} onValueChange={setActiveTab}>
						<TabsList className="mb-6 h-auto w-full flex-wrap justify-start gap-2 rounded-none bg-transparent p-0">
							<TabsTrigger
								value="wrapit"
								className={TAB_TRIGGER_CLASS}
								style={{ fontFamily: 'var(--font-display)' }}
							>
								Wrap It Ceremony
							</TabsTrigger>
							<TabsTrigger
								value="danceagain"
								className={TAB_TRIGGER_CLASS}
								style={{ fontFamily: 'var(--font-display)' }}
							>
								Dance Again
							</TabsTrigger>
						</TabsList>
						<TabsContent value="wrapit" className="mt-0">
							<VodPlayer title="Wrap It Ceremony" vodUrl={wrapItVodUrl!} />
						</TabsContent>
						<TabsContent value="danceagain" className="mt-0">
							<VodPlayer title="Dance Again" vodUrl={danceAgainVodUrl!} />
						</TabsContent>
					</Tabs>
				) : (
					<VodPlayer
						title={hasWrapIt ? 'Wrap It Ceremony' : 'Dance Again'}
						vodUrl={(wrapItVodUrl ?? danceAgainVodUrl)!}
					/>
				)}
			</section>
		</div>
	)
}
